import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { SoftwareLicencesInterface } from '../../interfaces/software-licences.interface';
import { EquipmentModel } from '../../models/complex/equipment.model';
import { TableModel } from '../../models/complex/table.model';
import { TableModelOptions } from '../../models/options/table-model-options';
import { ColumnModel } from '../../models/primitive/column.model';
import { LoadStatus } from '../../state/load-status';
import {
    hydrateSoftwareLicences,
    loadSoftwareLicences,
} from '../../state/software-licences/actions/software-licences.actions';
import {
    selectSoftwareLicencesDataForEquipment,
    selectSoftwareLicencesStatusForEquipment,
} from '../../state/software-licences/selectors/software-licences.selector';
import { SoftwareLicenceModel } from './software-licence.model';
import { softwareLicenceEquipmentKey } from '../../state/software-licences/software-licences.utils';

@Injectable({ providedIn: 'root' })
export class SoftwareLicencesNgrxService implements SoftwareLicencesInterface {
    public constructor(
        private readonly store: Store,
        private readonly translate: TranslateService,
    ) {
        this.store.dispatch(hydrateSoftwareLicences());
    }

    public getTableModelForSoftwareLicences(
        mainEquipment: EquipmentModel,
    ): Observable<TableModel<SoftwareLicenceModel>> {
        const columns: ColumnModel[] = [
            new ColumnModel(
                <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_DESCRIPTION'),
                'description',
            ),
            new ColumnModel(
                <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_MATERIAL_ID'),
                'materialNumber',
            ),
        ];

        const tableOptions = new TableModelOptions({ columns });

        return this.getSoftwareLicencesForEquipment(mainEquipment).pipe(
            map((licences: SoftwareLicenceModel[]) => new TableModel(licences, tableOptions)),
        );
    }

    public getSoftwareLicencesForEquipment(equipment: EquipmentModel): Observable<SoftwareLicenceModel[]> {
        const equipmentKey = softwareLicenceEquipmentKey(equipment);

        this.store.dispatch(
            loadSoftwareLicences({ equipmentKey, matnr: equipment.matnr.value, sernr: equipment.sernr.value }),
        );

        return this.store.select(selectSoftwareLicencesStatusForEquipment(equipmentKey)).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap(() => this.store.select(selectSoftwareLicencesDataForEquipment(equipmentKey))),
            take(1),
            map((data) => data.map((plain) => SoftwareLicenceModel.fromState(plain))),
        );
    }
}
